import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Statistic, Select, Typography, Table, Spin, Tag, Alert, Space, Button, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, SoundOutlined, DollarOutlined, WalletOutlined, BellOutlined, TeamOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, LoginOutlined, WarningOutlined, CalendarOutlined, MailOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

// Lightweight dependency-free grouped bar chart (Income / Expense / Profit per month).
function TrendBars({ rows }) {
  const max = Math.max(1, ...rows.flatMap(r => [r.income, r.expense, Math.abs(r.profit)]));
  const series = [{ key: 'income', label: 'Income', color: '#52c41a' }, { key: 'expense', label: 'Expense', color: '#ff4d4f' }, { key: 'profit', label: 'Profit', color: '#1890ff' }];
  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        {series.map(s => <span key={s.key} style={{ fontSize: 13 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: s.color, borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }} />{s.label}</span>)}
      </Space>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 220, overflowX: 'auto', paddingBottom: 4 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ flex: '1 0 70px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 180, width: '100%', justifyContent: 'center' }}>
              {series.map(s => {
                const val = s.key === 'profit' ? Math.abs(r.profit) : r[s.key];
                const h = Math.max(2, (val / max) * 175);
                return <div key={s.key} title={`${s.label}: ${inr(s.key === 'profit' ? r.profit : val)}`} style={{ width: 16, height: h, background: s.color, borderRadius: '3px 3px 0 0' }} />;
              })}
            </div>
            <Text style={{ fontSize: 12, marginTop: 6, whiteSpace: 'nowrap' }}>{r.label}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// Dependency-free SVG donut for distribution status.
function StatusDonut({ slices }) {
  const total = slices.reduce((a, s) => a + s.value, 0) || 1;
  const R = 70, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
      <svg width="180" height="180" viewBox="0 0 180 180">
        <g transform="rotate(-90 90 90)">
          {slices.map((s, i) => {
            const len = (s.value / total) * C;
            const el = <circle key={i} cx="90" cy="90" r={R} fill="none" stroke={s.color} strokeWidth="28" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} />;
            offset += len;
            return el;
          })}
        </g>
        <text x="90" y="96" textAnchor="middle" fontSize="26" fontWeight="700">{total}</text>
      </svg>
      <Space direction="vertical">
        {slices.map((s, i) => (
          <span key={i} style={{ fontSize: 14 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: s.color, borderRadius: 3, marginRight: 8, verticalAlign: 'middle' }} />
            {s.type}: <b>{s.value}</b> ({Math.round((s.value / total) * 100)}%)
          </span>
        ))}
      </Space>
    </div>
  );
}

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Year list runs from 3 years ago to next year, computed each render so it never goes stale.
const currentYear = new Date().getFullYear();
const yearOptions = [];
for (let y = currentYear - 3; y <= currentYear + 1; y++) yearOptions.push(y);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [alerts, setAlerts] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [syncingSheet, setSyncingSheet] = useState(false);
  const navigate = useNavigate();

  const syncToSheet = async () => {
    setSyncingSheet(true);
    try {
      await api.post('/gsheet/sync');
      message.success('Sab data Google Sheet me update ho gaya');
    } catch (err) {
      message.error(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncingSheet(false);
    }
  };

  const initialLoad = useRef(true);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/finance/dashboard', { params: { month, year } });
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    if (!silent) setLoading(false);
    initialLoad.current = false;
  }, [month, year]);

  const fetchAlerts = async () => {
    try {
      const res = await api.get('/finance/alerts');
      setAlerts(res.data?.alerts || []);
    } catch (err) { /* non-admin or table missing -> no alerts */ }
  };

  useEffect(() => {
    fetchDashboard();
    const poll = setInterval(() => fetchDashboard(true), 30000);
    return () => clearInterval(poll);
  }, [fetchDashboard]);

  useEffect(() => { fetchAlerts(); }, []);

  useEffect(() => {
    api.get('/staff/today-summary').then(r => setTodaySummary(r.data)).catch(() => {});
    api.get('/staff/leave-requests').then(r => setLeaveRequests(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const [sendingReport, setSendingReport] = useState(false);
  const sendDailyReport = async () => {
    setSendingReport(true);
    try {
      const res = await api.post('/staff/send-daily-report');
      message.success(res.data.message || 'Report sent!');
    } catch (e) { message.error(e.response?.data?.error || 'Failed to send report'); }
    setSendingReport(false);
  };

  const handleLeave = async (id, status, remark) => {
    try {
      await api.post('/staff/leave-approve', { id, status, admin_remark: remark || '' });
      api.get('/staff/leave-requests').then(r => setLeaveRequests(Array.isArray(r.data) ? r.data : []));
    } catch (e) {}
  };

  if (loading || !data) return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;

  const companyColumns = [
    { title: 'Company', dataIndex: 'company_name', key: 'company_name', render: (t) => <Text strong>{t}</Text> },
    { title: 'Income', dataIndex: 'total_income', key: 'income', render: (v) => <Text style={{ color: '#52c41a' }}>₹{v.toLocaleString('en-IN')}</Text> },
    { title: 'Expense', dataIndex: 'total_expense', key: 'expense', render: (v) => <Text style={{ color: '#ff4d4f' }}>₹{v.toLocaleString('en-IN')}</Text> },
    {
      title: 'Profit/Loss', dataIndex: 'profit', key: 'profit',
      render: (v) => <Tag color={v >= 0 ? 'green' : 'red'} style={{ fontSize: 14, padding: '4px 12px' }}>{v >= 0 ? '+' : ''}₹{v.toLocaleString('en-IN')}</Tag>
    }
  ];

  const trendColumns = [
    { title: 'Month', key: 'month', render: (_, r) => `${monthNames[r.month - 1]} ${r.year}` },
    { title: 'Income', dataIndex: 'income', render: (v) => `₹${v.toLocaleString('en-IN')}` },
    { title: 'Expense', dataIndex: 'expense', render: (v) => `₹${v.toLocaleString('en-IN')}` },
    { title: 'Profit', dataIndex: 'profit', render: (v) => <Tag color={v >= 0 ? 'green' : 'red'}>₹{v.toLocaleString('en-IN')}</Tag> }
  ];

  return (
    <div>
      {alerts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<BellOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b>Dhyaan dene wali baatein ({alerts.length})</b>}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {alerts.map((a, i) => (
                <div key={i} style={{ cursor: a.link ? 'pointer' : 'default' }} onClick={() => {
                  if (a.type === 'distribution') navigate('/songs?distribution_status=not_distributed');
                  else if (a.type === 'iprs') navigate('/songs?iprs_status=pending');
                  else if (a.type === 'poster') navigate('/songs?no_poster=1');
                  else if (a.link) navigate(a.link);
                }}>
                  <Tag color={a.level === 'error' ? 'red' : a.level === 'warning' ? 'orange' : 'blue'}>{a.count}</Tag>
                  <span>{a.text}</span>
                </div>
              ))}
            </Space>
          }
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col><Title level={3} style={{ margin: 0 }}>Dashboard</Title></Col>
        <Col>
          <Button icon={<CloudUploadOutlined />} loading={syncingSheet} onClick={syncToSheet} style={{ marginRight: 8 }}>
            Sync to Sheet
          </Button>
          <Select value={month} onChange={setMonth} style={{ width: 100, marginRight: 8 }}>
            {monthNames.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
          </Select>
          <Select value={year} onChange={setYear} style={{ width: 100 }}>
            {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
        </Col>
      </Row>

      {/* Overall Summary Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 12, background: 'linear-gradient(135deg, #52c41a22, #52c41a11)' }}>
            <Statistic
              title={<Text strong style={{ fontSize: 16 }}>Total Income</Text>}
              value={data.overall.total_income}
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              suffix=""
              formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`}
              valueStyle={{ color: '#52c41a', fontSize: 28 }}
            />
            {data.overall.total_income_usd > 0 && (
              <Text type="secondary" style={{ fontSize: 14 }}>≈ ${Number(data.overall.total_income_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (USD)</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 12, background: 'linear-gradient(135deg, #ff4d4f22, #ff4d4f11)' }}>
            <Statistic
              title={<Text strong style={{ fontSize: 16 }}>Total Expense</Text>}
              value={data.overall.total_expense}
              prefix={<WalletOutlined style={{ color: '#ff4d4f' }} />}
              formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`}
              valueStyle={{ color: '#ff4d4f', fontSize: 28 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 12, background: data.overall.profit >= 0 ? 'linear-gradient(135deg, #1890ff22, #1890ff11)' : 'linear-gradient(135deg, #ff4d4f22, #ff4d4f11)' }}>
            <Statistic
              title={<Text strong style={{ fontSize: 16 }}>Net {data.overall.profit >= 0 ? 'Profit' : 'Loss'}</Text>}
              value={Math.abs(data.overall.profit)}
              prefix={data.overall.profit >= 0 ? <ArrowUpOutlined style={{ color: '#1890ff' }} /> : <ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
              formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`}
              valueStyle={{ color: data.overall.profit >= 0 ? '#1890ff' : '#ff4d4f', fontSize: 28 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Today's Staff Attendance Summary */}
      {todaySummary && (
        <Card style={{ marginTop: 16, borderRadius: 12, background: 'linear-gradient(135deg, #e6f7ff, #f0f5ff)' }}
          title={<span><TeamOutlined /> Today's Staff Attendance — {todaySummary.date}</span>}
          extra={<Button icon={<MailOutlined />} size="small" type="primary" ghost loading={sendingReport} onClick={sendDailyReport}>Email Report</Button>}>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Card style={{ borderRadius: 10, textAlign: 'center', background: '#f6ffed' }}>
                <Statistic title="Present" value={todaySummary.present} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card style={{ borderRadius: 10, textAlign: 'center', background: '#fff2f0' }}>
                <Statistic title="Absent" value={todaySummary.absent} prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />} valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card style={{ borderRadius: 10, textAlign: 'center', background: '#fffbe6' }}>
                <Statistic title="On Leave" value={todaySummary.on_leave} prefix={<CalendarOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card style={{ borderRadius: 10, textAlign: 'center', background: '#fff0f6' }}>
                <Statistic title="Total Staff" value={todaySummary.total_staff} prefix={<TeamOutlined style={{ color: '#722ed1' }} />} valueStyle={{ color: '#722ed1' }} />
              </Card>
            </Col>
          </Row>
          {todaySummary.late_arrivals && todaySummary.late_arrivals.length > 0 && (
            <Alert type="warning" showIcon icon={<WarningOutlined />} style={{ marginTop: 12, borderRadius: 8 }}
              message={<b>Late Arrivals (after 11:00 AM)</b>}
              description={
                <div style={{ marginTop: 4 }}>
                  {todaySummary.late_arrivals.map((la, i) => (
                    <Tag key={i} color="orange" style={{ margin: 4, fontSize: 13 }}>
                      <LoginOutlined /> {la.full_name || la.username} — {la.check_in_time}
                    </Tag>
                  ))}
                </div>
              }
            />
          )}
          {todaySummary.attendance && todaySummary.attendance.length > 0 && (
            <Table size="small" style={{ marginTop: 12 }} pagination={false} rowKey="user_id"
              dataSource={todaySummary.attendance}
              columns={[
                { title: 'Staff', key: 'name', render: (_, r) => <Text strong>{r.full_name || r.username}</Text> },
                { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'present' ? 'green' : v === 'half_day' ? 'orange' : v === 'leave' ? 'blue' : 'red'}>{v}</Tag> },
                { title: 'Check-In', dataIndex: 'check_in_time', render: (v, r) => {
                  const isLate = v && v > '11:00:00';
                  return v ? <Tag color={isLate ? 'red' : 'green'}><LoginOutlined /> {v} {isLate ? '(LATE)' : ''}</Tag> : <Text type="secondary">—</Text>;
                }},
                { title: 'Check-Out', dataIndex: 'check_out_time', render: v => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text> },
              ]}
            />
          )}
        </Card>
      )}

      {/* Pending Leave Requests */}
      {leaveRequests.filter(lr => lr.status === 'pending').length > 0 && (
        <Card style={{ marginTop: 16, borderRadius: 12, background: '#fffbe6', borderLeft: '4px solid #faad14' }}
          title={<span><CalendarOutlined /> Pending Leave Requests ({leaveRequests.filter(lr => lr.status === 'pending').length})</span>}>
          <Table size="small" rowKey="id" pagination={false}
            dataSource={leaveRequests.filter(lr => lr.status === 'pending')}
            columns={[
              { title: 'Staff', key: 'name', render: (_, r) => <Text strong>{r.full_name || r.username}</Text> },
              { title: 'Type', dataIndex: 'leave_type', render: v => <Tag color={v === 'sick' ? 'red' : v === 'earned' ? 'blue' : 'orange'}>{(v || '').toUpperCase()}</Tag> },
              { title: 'From', dataIndex: 'from_date' },
              { title: 'To', dataIndex: 'to_date' },
              { title: 'Days', dataIndex: 'days' },
              { title: 'Reason', dataIndex: 'reason', render: v => v || '—' },
              { title: 'Action', key: 'action', render: (_, r) => (
                <Space>
                  <Button size="small" type="primary" onClick={() => handleLeave(r.id, 'approved')}>Approve</Button>
                  <Button size="small" danger onClick={() => handleLeave(r.id, 'rejected')}>Reject</Button>
                </Space>
              )},
            ]}
          />
        </Card>
      )}

      {/* Song Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Card style={{ borderRadius: 12, cursor: 'pointer' }} hoverable onClick={() => navigate('/songs')}>
            <Statistic title="Total Songs" value={data.songs.total} prefix={<SoundOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card style={{ borderRadius: 12, cursor: 'pointer' }} hoverable onClick={() => navigate('/songs?distribution_status=distributed')}>
            <Statistic title="Distributed" value={data.songs.distributed} valueStyle={{ color: '#52c41a' }} suffix={<Tag color="green">Done</Tag>} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card style={{ borderRadius: 12, cursor: 'pointer' }} hoverable onClick={() => navigate('/songs?distribution_status=not_done')}>
            <Statistic title="Not Distributed" value={(data.songs.not_distributed || 0) + (data.songs.pending || 0)} valueStyle={{ color: '#ff4d4f' }} suffix={<Tag color="red">Pending</Tag>} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card style={{ borderRadius: 12, cursor: 'pointer' }} hoverable onClick={() => navigate('/songs?iprs_status=pending')}>
            <Statistic title="IPRS Pending" value={data.songs.iprs_pending || 0} valueStyle={{ color: '#fa8c16' }} prefix={<WarningOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card style={{ borderRadius: 12, cursor: 'pointer' }} hoverable onClick={() => navigate('/songs?no_poster=1')}>
            <Statistic title="No Poster" value={data.songs.no_poster || 0} valueStyle={{ color: '#8c8c8c' }} prefix={<CloseCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Company-wise Breakdown */}
      <Card title="Company-wise Breakdown" style={{ marginTop: 24, borderRadius: 12 }}>
        <Table
          dataSource={data.companies}
          columns={companyColumns}
          rowKey="company_id"
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Monthly Trend — chart + table */}
      <Card title="Monthly Trend (Last 6 Months)" style={{ marginTop: 24, borderRadius: 12 }}>
        {data.monthly_trend && data.monthly_trend.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <TrendBars rows={data.monthly_trend} />
          </div>
        )}
        <Table
          dataSource={data.monthly_trend}
          columns={trendColumns}
          rowKey={(r) => `${r.month}-${r.year}`}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Distribution status pie */}
      {data.songs && data.songs.total > 0 && (
        <Card title="Song Distribution Status" style={{ marginTop: 24, borderRadius: 12 }}>
          <StatusDonut
            slices={[
              { type: 'Distributed', value: data.songs.distributed, color: '#52c41a' },
              { type: 'Pending', value: data.songs.pending, color: '#faad14' },
              { type: 'Not Distributed', value: data.songs.not_distributed, color: '#ff4d4f' },
            ].filter(d => d.value > 0)}
          />
        </Card>
      )}
    </div>
  );
}
